import { useState, useEffect } from "react";
import { db } from "../../../services/firebaseService/connection";
import { getDocs, collection } from "firebase/firestore";
import { Input, Table, message } from "antd";
import "./logos.css";
import SuperAdminContainer from "../SuperAdminContainer";
import { ScreenHeader } from "../../../CommonComponents";

function normalizeImageUrl(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const t = value.trim();
    return t || null;
  }
  if (typeof value === "object") {
    const url =
      (typeof value.url === "string" && value.url.trim()) ||
      (typeof value.downloadURL === "string" && value.downloadURL.trim()) ||
      (typeof value.src === "string" && value.src.trim()) ||
      null;
    return url || null;
  }
  return null;
}

function rgbObjectToHex(rgb) {
  if (!rgb || rgb.r == null || rgb.g == null || rgb.b == null) return null;
  const clamp = (n) =>
    Math.max(0, Math.min(255, Math.round(Number(n)) || 0));
  const r = clamp(rgb.r);
  const g = clamp(rgb.g);
  const b = clamp(rgb.b);
  return (
    "#" +
    [r, g, b]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()
  );
}

function renderColorCell(rgb) {
  const hex = rgbObjectToHex(rgb);
  if (!hex) {
    return <span style={{ color: "#999" }}>—</span>;
  }
  return (
    <button
      type="button"
      className="superadmin-logos-color-cell"
      title="Copy hex"
      onClick={() => {
        navigator.clipboard
          .writeText(hex)
          .then(() => message.success(`Copied ${hex}`))
          .catch(() => message.error("Could not copy"));
      }}
    >
      <span
        className="superadmin-logos-color-swatch"
        style={{ backgroundColor: hex }}
        aria-hidden
      />
      <span className="superadmin-logos-color-hex">{hex}</span>
    </button>
  );
}

const handleImageDownload = (src, fileName) => {
  fetch(src)
    .then((response) => {
      if (!response.ok) throw new Error("Network response was not ok");
      return response.blob();
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.download = fileName;
      a.href = blobUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      console.error("There was a problem with the fetch operation:", error);
    });
};

const columns = [
  {
    title: "Group",
    dataIndex: "groupName",
    key: "groupName",
    className: "header-white",
    width: 150,
  },
  {
    title: "Club",
    dataIndex: "clubName",
    key: "clubName",
    className: "header-white",
    width: 150,
  },
  {
    title: "League",
    dataIndex: "leagueName",
    key: "leagueName",
    className: "header-white",
    width: 150,
  },
  {
    title: "Primary",
    dataIndex: "primaryColor",
    key: "primaryColor",
    className: "header-white",
    width: 160,
    render: (rgb) => renderColorCell(rgb),
  },
  {
    title: "Secondary",
    dataIndex: "secondaryColor",
    key: "secondaryColor",
    className: "header-white",
    width: 160,
    render: (rgb) => renderColorCell(rgb),
  },
  {
    title: "Logo",
    dataIndex: "logo",
    key: "logo",
    className: "header-white",
    width: 150,
    render: (data, allData) => {
      const logoUrl = normalizeImageUrl(data);
      if (!logoUrl) {
        return <span style={{ color: "#999" }}>—</span>;
      }
      return (
        <div className="superadmin-logo-container">
          <img
            onClick={() => handleImageDownload(logoUrl, allData.groupName)}
            className="superadmin-logos-img"
            src={logoUrl}
            style={{ width: 50, height: 50, borderRadius: 25, marginRight: 10 }}
            alt=""
          />
        </div>
      );
    },
  },
];

const Design = () => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const snap = await getDocs(collection(db, "group"));
        const groups = [];
        snap.docs.forEach((docSnap) => {
          const firebaseData = docSnap.data() || {};
          const configuration = firebaseData.configuration || {};
          groups.push({
            key: docSnap.id,
            groupName: firebaseData.groupName,
            clubName: firebaseData.clubName,
            leagueName: firebaseData.leagueName,
            logo: configuration.logo,
            primaryColor: configuration.primaryColor,
            secondaryColor: configuration.secondaryColor,
          });
        });
        groups.sort((a, b) => (a.clubName || "").localeCompare(b.clubName || ""));
        setTableData(groups);
        setFilteredData(groups);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const onFilterChange = (name) => {
    setSearchValue(name);
    const filtered = tableData.filter((group) =>
      (group.groupName || "").toLowerCase().includes(name.toLowerCase())
    );
    setFilteredData(filtered);
  };

  return (
    <SuperAdminContainer>
      <ScreenHeader title="Design" elementCount={filteredData.length} />
      <div
        style={{
          marginTop: "2px",
          backgroundColor: "var(--primary-white-background)",
          padding: "8px 12px",
        }}
      >
        <Input
          style={{ maxWidth: "350px" }}
          value={searchValue}
          onChange={(e) => onFilterChange(e.target.value)}
          allowClear
          name="search"
          placeholder="Search by group"
          tabIndex="2"
        />
      </div>

      <Table
        columns={columns}
        bordered
        dataSource={filteredData}
        loading={isLoading}
        bodyStyle={{ backgroundColor: "#ffffff" }}
        scroll={{ x: true }}
        pagination={{
          position: ["bottomLeft"],
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
        className="superadmin-logos-table table-backdrop mb-3"
      />
    </SuperAdminContainer>
  );
};

export default Design;
